import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Stack, Group, Switch, Button, Badge,
  ActionIcon, Divider, NumberInput, SimpleGrid, Paper,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBell, IconPlus, IconTrash, IconClock, IconAlertTriangle } from '@tabler/icons-react'
import { getReminderSchedules, updateReminderSchedule } from '../api/client'
import { useBreakpoint } from '../hooks/useBreakpoint'

const PRESET_SLOTS = ['07:00','08:00','09:00','12:00','13:00','15:00','17:00','18:00','20:00']

interface Schedule {
  orderType: string
  timeSlots: string[]
  staleAfterHours: number
  enabled: boolean
}

function ReminderCard({
  title, color, schedule, onChange
}: {
  title: string; color: string
  schedule: Schedule
  onChange: (s: Schedule) => void
}) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [local, setLocal] = useState<Schedule>(schedule)

  useEffect(() => { setLocal(schedule) }, [schedule])

  const addSlot = (time: string) => {
    if (local.timeSlots.includes(time)) return
    setLocal(p => ({ ...p, timeSlots: [...p.timeSlots, time].sort() }))
  }
  const removeSlot = (time: string) =>
    setLocal(p => ({ ...p, timeSlots: p.timeSlots.filter(t => t !== time) }))

  const save = async () => {
    setSaving(true)
    try {
      await updateReminderSchedule(local.orderType, {
        timeSlots: local.timeSlots,
        staleAfterHours: local.staleAfterHours,
        enabled: local.enabled,
      })
      onChange(local)
      notifications.show({ color: 'teal', message: t('common.success') })
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setSaving(false) }
  }

  return (
    <Paper withBorder radius="md" p="lg">
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Box style={{ width: 12, height: 12, borderRadius: '50%', background: color }} />
          <Text fw={600} style={{ fontSize: 15 }}>{title}</Text>
        </Group>
        <Switch
          checked={local.enabled}
          onChange={e => setLocal(p => ({ ...p, enabled: e.currentTarget.checked }))}
          label={local.enabled ? t('common.active','ใช้งาน') : t('common.inactive','ปิด')}
          color="teal"
        />
      </Group>

      <Divider mb="md" />

      {/* Option A — Time Slots */}
      <Stack gap="sm" mb="lg">
        <Group gap="xs">
          <IconClock size={16} color="#0d9488" />
          <Text size="sm" fw={600}>{t('reminder.optionA','แจ้งเตือนตามเวลาที่กำหนด')}</Text>
        </Group>
        <Text size="xs" c="dimmed">{t('reminder.optionAHint','ระบบจะส่งสรุปงานค้างเข้ากลุ่ม LINE ทุกรอบที่กำหนด')}</Text>

        {/* Active slots */}
        <Group gap="xs" wrap="wrap">
          {local.timeSlots.map(slot => (
            <Badge key={slot} size="lg" variant="light" color="teal"
              rightSection={
                <ActionIcon size="xs" variant="transparent" color="teal"
                  onClick={() => removeSlot(slot)}>
                  <IconTrash size={10} />
                </ActionIcon>
              }
            >
              🕐 {slot}
            </Badge>
          ))}
          {local.timeSlots.length === 0 && (
            <Text size="xs" c="dimmed">{t('reminder.noSlots','ยังไม่มีเวลาที่กำหนด')}</Text>
          )}
        </Group>

        {/* Preset buttons */}
        <Text size="xs" c="dimmed">{t('reminder.addPreset','เพิ่มเวลาแจ้งเตือน:')}</Text>
        <Group gap={6} wrap="wrap">
          {PRESET_SLOTS.map(slot => (
            <Button key={slot} size="xs" variant={local.timeSlots.includes(slot) ? 'filled' : 'outline'}
              color="teal" leftSection={<IconPlus size={11} />}
              onClick={() => addSlot(slot)} disabled={local.timeSlots.includes(slot)}>
              {slot}
            </Button>
          ))}
        </Group>
      </Stack>

      <Divider mb="md" />

      {/* Option C — Stale threshold */}
      <Stack gap="sm" mb="lg">
        <Group gap="xs">
          <IconAlertTriangle size={16} color="#f59e0b" />
          <Text size="sm" fw={600}>{t('reminder.optionC','แจ้งเตือนงานที่ค้างนาน')}</Text>
        </Group>
        <Text size="xs" c="dimmed">{t('reminder.optionCHint','ถ้างานค้างนานเกินกำหนด ระบบจะแจ้งเตือนซ้ำทุก 1 ชั่วโมง (0 = ปิดใช้งาน)')}</Text>
        <Group gap="sm" align="flex-end">
          <NumberInput
            label={t('reminder.staleHours','แจ้งเตือนเมื่อค้างนานกว่า (ชั่วโมง)')}
            value={local.staleAfterHours}
            onChange={v => setLocal(p => ({ ...p, staleAfterHours: Number(v) || 0 }))}
            min={0} max={72} step={1} w={220}
            placeholder="0 = ปิด"
            description={local.staleAfterHours > 0
              ? `จะแจ้งเตือนทุก 1 ชม. เมื่อมีงานค้างเกิน ${local.staleAfterHours} ชั่วโมง`
              : 'ปิดการแจ้งเตือนงานค้างนาน'}
          />
          {local.staleAfterHours > 0 && (
            <Badge color="orange" variant="light" size="lg">
              ⏰ {'>'}{local.staleAfterHours} ชม. → แจ้งเตือน
            </Badge>
          )}
        </Group>
      </Stack>

      <Group justify="flex-end">
        <Button loading={saving} onClick={save}
          style={{ background: 'var(--brand-primary,#0f172a)', color: 'var(--brand-on-primary,#fff)', border: '1px solid var(--brand-border,transparent)' }}>
          {t('common.save','บันทึก')}
        </Button>
      </Group>
    </Paper>
  )
}

export default function ReminderSettingsPage() {
  const { t } = useTranslation()
  const { isSmall } = useBreakpoint()
  const [loading, setLoading] = useState(true)
  const [woSchedule, setWoSchedule] = useState<Schedule>({ orderType: 'workorder', timeSlots: ['08:00'], staleAfterHours: 0, enabled: true })
  const [hkSchedule, setHkSchedule] = useState<Schedule>({ orderType: 'hkorder', timeSlots: ['08:00'], staleAfterHours: 0, enabled: true })

  useEffect(() => {
    getReminderSchedules().then(({ data }) => {
      if (data.workorder) setWoSchedule(data.workorder)
      if (data.hkorder)   setHkSchedule(data.hkorder)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  return (
    <Box p={isSmall ? 16 : 32} maw={760}>
      <Group gap="sm" mb={4}>
        <IconBell size={22} color="#0d9488" />
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
          {t('reminder.title','การตั้งค่าการแจ้งเตือน')}
        </Text>
      </Group>
      <Text size="sm" c="dimmed" mb="xl">
        {t('reminder.subtitle','ตั้งเวลาแจ้งเตือนงานค้างเข้ากลุ่ม LINE — รองรับหลายรอบต่อวัน และแจ้งซ้ำเมื่อค้างนาน')}
      </Text>

      {loading ? (
        <Text c="dimmed" size="sm">{t('common.loading','กำลังโหลด...')}</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <ReminderCard
            title={`🔧 Work Order`}
            color="#0d9488"
            schedule={woSchedule}
            onChange={setWoSchedule}
          />
          <ReminderCard
            title={`🛎️ HK Order`}
            color="#6d28d9"
            schedule={hkSchedule}
            onChange={setHkSchedule}
          />
        </SimpleGrid>
      )}

      <Paper withBorder radius="md" p="md" mt="lg" style={{ background: '#f8fafc' }}>
        <Group gap="xs" mb={6}><IconClock size={14} color="#64748b" /><Text size="xs" fw={600} c="dimmed">วิธีการทำงาน</Text></Group>
        <Stack gap={4}>
          <Text size="xs" c="dimmed">🔔 <b>Option A</b> — ระบบส่งสรุปงานค้างทั้งหมดเข้ากลุ่ม LINE ตามเวลาที่ตั้งไว้ทุกวัน</Text>
          <Text size="xs" c="dimmed">⏰ <b>Option C</b> — ถ้างานใดค้างนานเกินกำหนด ระบบแจ้งเตือนซ้ำทุก 1 ชั่วโมงพร้อมระบุว่าค้างมากี่ชั่วโมง</Text>
          <Text size="xs" c="dimmed">✅ ทั้ง Work Order และ HK Order ตั้งค่าแยกกันได้อิสระ</Text>
        </Stack>
      </Paper>
    </Box>
  )
}
